import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../services/auth';
import { LogIn, AlertCircle, CheckCircle2, ArrowLeft, Lock, Mail, Eye, EyeOff, X, ArrowUpRight } from 'lucide-react';
import axios from 'axios';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  const navigate = useNavigate();

  const [isBlocked, setIsBlocked] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setIsBlocked(false);

    try {
      await login(email, password);
      navigate('/admin');
    } catch (err: any) {
      if (err.message === 'ACCOUNT_BLOCKED') {
        setIsBlocked(true);
      } else {
        setError(err.message || 'Falha ao entrar. Verifique suas credenciais.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-white font-sans">

      {/* MODAL CONTA BLOQUEADA */}
      {isBlocked && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-white w-full max-w-sm rounded-3xl p-8 shadow-2xl relative text-center border-t-4 border-red-500">
            <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-200 shadow-inner">
              <Lock size={32} />
            </div>
            <h3 className="text-2xl font-black text-surface-900 mb-2 tracking-tight">Acesso Interrompido</h3>
            <p className="text-surface-500 mb-6 font-medium text-sm leading-relaxed">
              O seu acesso à ZapShop foi temporariamente bloqueado. Para regularizar a situação ou obter mais informações, entre em contato com o nosso suporte.
            </p>
            <a
              href="https://wa.me/5521991000120?text=Olá,%20meu%20acesso%20ao%20ZapShop%20está%20bloqueado.%20Preciso%20de%20ajuda."
              target="_blank"
              rel="noreferrer"
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-black py-3.5 rounded-xl transition-all shadow-lg shadow-emerald-500/30 flex items-center justify-center gap-2 mb-3"
            >
              Falar com Suporte <ArrowUpRight size={18} />
            </a>
            <button
              onClick={() => setIsBlocked(false)}
              className="text-surface-400 hover:text-surface-600 font-bold text-xs uppercase tracking-widest transition-colors w-full py-2"
            >
              Voltar ao Login
            </button>
          </div>
        </div>
      )}

      {/* LADO ESQUERDO: BRANDING (Visível apenas em telas grandes) */}
      <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-brand-900 via-brand-700 to-secondary-600 relative overflow-hidden items-center justify-center p-12">
        {/* Elementos Decorativos de Fundo */}
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white/10 to-transparent opacity-40"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-secondary-500 rounded-full blur-[100px] opacity-20"></div>

        {/* Conteúdo da Marca */}
        <div className="relative z-10 text-center text-white max-w-lg">
          <img
            src="/logozapshopsaas.png"
            alt="ZapShop Logo"
            className="h-40 w-auto mx-auto mb-8 drop-shadow-2xl hover:scale-105 transition-transform duration-500"
          />
          <h1 className="text-5xl font-bold mb-6 tracking-tight">Gerencie sua Loja com Poder.</h1>
          <p className="text-xl text-brand-50 opacity-90 leading-relaxed">
            A plataforma completa para transformar seu WhatsApp em uma máquina de vendas automática.
          </p>
        </div>
      </div>

      {/* LADO DIREITO: FORMULÁRIO */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-8 bg-surface-50">
        <div className="w-full max-w-md bg-white p-8 rounded-3xl shadow-xl border border-surface-100">

          {/* Cabeçalho Mobile (Logo pequena se estiver em mobile) */}
          <div className="lg:hidden text-center mb-8">
            <img src="/logozapshopsaas.png" alt="ZapShop" className="h-16 mx-auto" />
          </div>

          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-surface-900">Bem-vindo de volta!</h2>
            <p className="text-surface-500 mt-2">Acesse o painel para gerenciar seus pedidos.</p>
          </div>

          {/* Mensagem de Erro */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-100 text-red-600 p-4 rounded-xl flex items-center gap-3 text-sm animate-fade-in">
              <AlertCircle size={20} className="shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {/* Card de Demo (Útil para testes) */}
          <div className="mb-8 bg-brand-50/50 border border-brand-100 p-4 rounded-xl flex items-start gap-3 text-sm text-brand-800">
            <CheckCircle2 size={18} className="shrink-0 mt-0.5 text-brand-600" />
            <div>
              <p className="font-bold text-brand-700">Credenciais de Teste:</p>
              <p className="text-brand-600/80">Email: teste@gmail.com</p>
              <p className="text-brand-600/80">Senha: teste</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-bold text-surface-600 ml-1">Email Corporativo</label>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-400 group-focus-within:text-brand-500 transition-colors" size={20} />
                <input
                  type="email"
                  required
                  className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-surface-200 bg-surface-50 focus:bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all font-medium text-surface-900 placeholder:text-surface-400"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between ml-1">
                <label className="text-sm font-bold text-surface-600">Sua Senha</label>
                <button type="button" onClick={() => setShowForgotModal(true)} className="text-sm text-brand-600 hover:text-brand-700 font-medium">Esqueceu a senha?</button>
              </div>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-400 group-focus-within:text-brand-500 transition-colors" size={20} />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  className="w-full pl-12 pr-12 py-3.5 rounded-xl border border-surface-200 bg-surface-50 focus:bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all font-medium text-surface-900 placeholder:text-surface-400"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-surface-400 hover:text-brand-500 transition-colors"
                  title={showPassword ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-700 hover:to-brand-600 text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-brand-500/30 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center gap-2 text-lg"
            >
              {loading ? (
                <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Entrar no Sistema <LogIn size={20} />
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-surface-100 text-center">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-surface-500 hover:text-brand-600 font-medium transition-colors"
            >
              <ArrowLeft size={18} /> Voltar para a Loja
            </button>
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-surface-400">
          &copy; 2026 ZapShop. Produto da família <a href="https://viniciusomatos.com/" target="_blank" rel="noreferrer" className="font-bold text-brand-600 hover:underline">Vinícius O Matos</a>
        </p>
      </div>

      {/* MODAL ESQUECEU A SENHA */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white w-full max-w-sm rounded-3xl p-6 shadow-2xl relative">
            <button onClick={() => { setShowForgotModal(false); setForgotSuccess(false); setForgotEmail(''); }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={20} /></button>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Recuperar Senha</h3>
            {forgotSuccess ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle2 size={32} /></div>
                <p className="text-sm text-gray-600">Se o e-mail existir em nossa base, você receberá as instruções em instantes.</p>
                <button onClick={() => { setShowForgotModal(false); setForgotSuccess(false); setForgotEmail(''); }} className="mt-6 w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-3 rounded-xl transition-colors">Voltar ao Login</button>
              </div>
            ) : (
              <form onSubmit={async (e) => {
                e.preventDefault();
                setForgotLoading(true);
                try {
                  await axios.post('https://zapshop.app/api/auth/forgot-password', { email: forgotEmail });
                } catch (err) {
                  // Fallback silencioso (boas práticas de segurança)
                } finally {
                  setForgotLoading(false);
                  setForgotSuccess(true);
                }
              }}>
                <p className="text-sm text-gray-500 mb-4">Digite seu e-mail corporativo para receber as instruções de redefinição.</p>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase">E-mail</label>
                    <input type="email" required value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} className="w-full mt-1 p-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white outline-none focus:ring-2 focus:ring-brand-500" placeholder="seu@email.com" />
                  </div>
                  <button disabled={forgotLoading} className="w-full bg-brand-600 hover:bg-brand-700 text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center">
                    {forgotLoading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Enviar Instruções'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
